/**
 * The external clinical entry: care recorded from outside the clinic, its
 * dedicated page, lifecycle, translations, and the entries an AI processing
 * proposes from patient documents.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const externalEntry = {
  "externalEntry.fileUnavailable": "Linked file is unavailable.",
  "externalEntry.approximateDate": "Approximately {date}",
  "externalEntry.recordedBy": "Recorded by",
  "externalEntry.title": "External medical entry",
  "externalEntry.external": "External",
  "externalEntry.statusAmending": "Amending",
  "externalEntry.careDetails": "Care details",
  "externalEntry.place": "Place",
  "externalEntry.eventDate": "Care date",
  "externalEntry.dateHint": "Enter a year, year and month, or full date. Year and month precision is approximate.",
  "externalEntry.approximate": "Approximate",
  "externalEntry.approximateMark": "≈",
  "externalEntry.heading": "Title",
  "externalEntry.diagnoses": "Diagnoses",
  "externalEntry.diagnosisHint": "Optional. Each diagnosis is a code from the dictionary or the source’s own words. A code does not confirm the diagnosis clinically; these diagnoses remain in this external entry.",
  "externalEntry.diagnosisReadHint": "Matching a code does not confirm the diagnosis clinically. These diagnoses remain in this external entry.",
  "externalEntry.diagnosesEmpty": "No diagnoses recorded.",
  "externalEntry.addDiagnosis": "Add diagnosis",
  "externalEntry.diagnosisAddHint": "Choose codes in the diagnosis dictionary panel, or write a diagnosis the dictionary does not hold as text.",
  "externalEntry.newDiagnosisText": "Diagnosis as text",
  "externalEntry.addDiagnosisText": "Add as text",
  "externalEntry.diagnosisText": "Diagnosis text",
  "externalEntry.diagnosisAsText": "Text",
  "externalEntry.done": "Done",
  "externalEntry.diagnosisUnsavedIntent": "External medical entry — a diagnosis typed but not added",
  "externalEntry.doctor": "External doctor",
  "externalEntry.clinic": "External clinic",
  "externalEntry.findOrAddName": "Find or add a name",
  "externalEntry.addName": "Add “{name}”",
  "externalEntry.newNameHint": "New name: added to the directory when the entry is saved.",
  "externalEntry.manageName": "Rename or merge saved name",
  "externalEntry.globalNameHint": "This changes the saved name in every current entry that uses it. Earlier saved versions retain the original label.",
  "externalEntry.rename": "Rename",
  "externalEntry.mergeInto": "Keep this saved name",
  "externalEntry.merge": "Merge",
  "externalEntry.country": "Country",
  "externalEntry.city": "City",
  "externalEntry.source": "Information source",
  "externalEntry.files": "Patient files linked to this entry",
  "externalEntry.filesEmpty": "No files are linked to this entry.",
  "externalEntry.aiPages": "AI: p. {pages}",
  "externalEntry.aiWhole": "AI: whole file",
  "externalEntry.viewFile": "View file",
  "externalEntry.linkFile": "Link a patient file",
  "externalEntry.chooseFile": "Choose a file",
  "externalEntry.link": "Link",
  "externalEntry.unlink": "Unlink",
  "externalEntry.unlinkFile": "Unlink {name}",
  "externalEntry.upload": "Upload and link a file",
  "externalEntry.uploading": "Uploading…",
  "externalEntry.uploadFailed": "The file could not be uploaded.",
  "externalEntry.filesSavedWithEntry": "Linked files are saved with the entry. An uploaded file is added to Patient Files at once.",
  "externalEntry.filesLocked": "Linked files change only in a draft or while the entry is being amended.",
  "externalEntry.remove": "Remove",
  "externalEntry.saving": "Saving…",
  "externalEntry.required": "Enter clinical text and at least the year of care.",
  "externalEntry.loadFailed": "The external entry could not be loaded.",
  "externalEntry.writeFailed": "Could not save. Check the fields and permissions. If this is a conflict, reload the current entry and review your draft.",
  "externalEntry.writeForbidden": "You do not have permission to change this entry.",
  "externalEntry.notEditable": "This entry can no longer be changed in its current state. Reload the page to see it as it is.",
  "externalEntry.conflict": "This entry was changed elsewhere. Load the saved revision to continue; your unsaved edits will be discarded.",
  "externalEntry.changedElsewhere": "The entry was changed elsewhere. Its current version is loaded; review it and try again.",
  "externalEntry.unsavedIntent": "External medical entry — edits that were never saved",
  "externalEntry.translationUnsavedIntent": "External medical entry translation — edits that were never saved",
  "externalEntry.completedNotice": "This external entry is completed and read-only. Amend it to change it.",
  "externalEntry.completeConfirmBody": "Completing makes this entry final. Later changes need an amendment with a reason.",
  "externalEntry.completeCorrectionConfirmBody": "Completing the amendment saves it as the entry's amended version. The earlier version stays in the history.",
  "externalEntry.amendBody": "Choose the reason. The entry opens for editing with the same content. Its completed version stays in the history, and completing the amendment saves the amended version.",
  "externalEntry.mergedReadOnly": "This patient was merged into another chart. The entry is read-only.",
  "externalEntry.deskOnly": "This entry is read-only here. Editing, completing and correcting it are on the desktop.",
  "externalEntry.navDescription": "Care details, protocol and diagnoses",
  "externalEntry.navProposed": "AI: proposed entry",
  "externalEntry.navFiles": "Files",
  "externalEntry.navFilesDescription": "Documents the entry was recorded from",
  "externalEntry.printUnavailable": "Printing an external entry is not available yet.",
  "externalEntry.namesFailed": "Saved names could not be loaded. A new name can still be entered.",
  "externalEntry.loading": "Loading…",
  "externalEntry.filesFailed": "Patient files could not be loaded.",
  "externalEntry.reload": "Reload",
  "externalEntry.reloadFailed": "Reload failed. Your draft is preserved.",
  "externalEntry.reloaded": "The saved entry is loaded. Your form now shows its current values.",
  "externalEntry.history": "Entry history",
  "externalEntry.historyFailed": "The entry history could not be loaded.",
  "externalEntry.earlier": "Earlier revisions",
  "externalEntry.versionNotice": "Showing revision {revision}, saved {date}.",
  "externalEntry.versionTranslations": "Translations:",
  "externalEntry.backToCurrent": "Back to the current entry",
  "externalEntry.aiTitle": "External entries from documents",
  "externalEntry.sourceWarnings": "The source contains uncertainties. Check this entry against the documents.",
  "externalEntry.summaryGroup": "External clinical entries",
  "externalEntry.recognition.included": "To be read",
  "externalEntry.recognition.excluded": "Left out",
  "externalEntry.recognition.reasonUnlinked": "Not yet registered as patient documents.",
  "externalEntry.recognition.reasonBusy": "Already in an unfinished processing.",
  "externalEntry.recognition.openProcessing": "Open the processing",
  "externalEntry.recognition.reasonUnreadable": "Their text cannot be read.",
  "externalEntry.recognition.reasonOverLimit": "One processing reads at most {limit} documents.",
  "externalEntry.recognition.needsOcr": "Text recognition first: {count}",
  "externalEntry.recognition.ocrRefused": "These documents need text recognition, which you cannot start: it takes the right to process documents and the text recognition function switched on.",
  "externalEntry.recognition.pages": "Pages: {pages} of at most {limit}",
  "externalEntry.recognition.pagesUnknown": "Documents whose page count is not known yet: {count}",
  "externalEntry.recognition.pagesKnown": "Pages known so far: {pages} of at most {limit}",
  "externalEntry.recognition.pagesAfterOcr": "The page count is known once the text is recognized; one processing reads at most {limit} pages.",
  "externalEntry.recognition.pagesOver": "That is more pages than one processing reads. Leave some documents out.",
  "externalEntry.recognition.alreadySource": "Already a source of {codes}",
  "externalEntry.recognition.alreadySourceCount": "Already a source of EMR entries: {count}",
  "externalEntry.recognition.none": "None of the selected documents can be read.",
  "externalEntry.recognition.checking": "Checking the documents…",
  "externalEntry.recognition.checkFailed": "The documents could not be checked.",
  "externalEntry.recognition.checkAgain": "Check again",
  "externalEntry.recognition.startFailed": "The processing could not be started.",
  "externalEntry.recognition.startBusy": "Some documents went into another processing meanwhile; the list now shows where they are.",
  "externalEntry.recognition.startLimit": "There are more documents or pages than one processing reads.",
  "externalEntry.recognition.startForbidden": "You are not allowed to start this processing.",
  "externalEntry.recognition.stagesLabel": "Stages",
  "externalEntry.recognition.documentsTitle": "Documents",
  "externalEntry.recognition.ocr.notNeeded": "Text was already recognized",
  "externalEntry.recognition.ocr.waiting": "Waiting for text recognition",
  "externalEntry.recognition.ocr.running": "Recognizing text",
  "externalEntry.recognition.ocr.succeeded": "Text recognized",
  "externalEntry.recognition.ocr.failed": "Text recognition failed",
  "externalEntry.recognition.ocr.cancelled": "Text recognition was cancelled",
  "externalEntry.recognition.ocr.leftOut": "Left out",
  "externalEntry.recognition.ocrPercent": "Done: {percent}%",
  "externalEntry.recognition.documentPages": "Pages: {pages}",
  "externalEntry.recognition.segmented": "Pages marked: {done} of {total}",
  "externalEntry.recognition.events": "Care events found: {count}",
  "externalEntry.recognition.filled": "Entries filled: {done} of {total}",
  "externalEntry.recognition.executor": "AI requests are made on behalf of: {name}",
  "externalEntry.recognition.attempt": "Attempt: {attempt}",
  "externalEntry.recognition.failedAt": "Stopped at the stage: {stage}",
  "externalEntry.recognition.failedDocuments": "Documents with an error",
  "externalEntry.recognition.ocrDocumentFailed": "The text of this document could not be recognized.",
  "externalEntry.recognition.leftOutNote": "Documents whose text cannot be read are left out; the processing reads the rest.",
  "externalEntry.recognition.retry": "Retry",
  "externalEntry.recognition.retrying": "Retrying…",
  "externalEntry.recognition.retryFailed": "The processing could not be retried.",
  "externalEntry.recognition.retryForbidden": "You are not allowed to retry this processing.",
  "externalEntry.recognition.error.ocrFailed": "Text could not be recognized in some of the documents.",
  "externalEntry.recognition.error.documentUnreadable": "No document has text that can be read, or a document changed after the processing started.",
  "externalEntry.recognition.error.pageLimitExceeded": "Once their text was recognized, the documents had more pages than one processing reads.",
  "externalEntry.recognition.error.inputTooLarge": "The text was too large for the AI to read in one request.",
  "externalEntry.recognition.error.aiFunctionDisabled": "This AI function is switched off in the clinic settings.",
  "externalEntry.recognition.error.aiKeyMissing": "There is no AI access key for the account the requests are made on behalf of.",
  "externalEntry.recognition.error.accessLost": "The account the processing ran on behalf of no longer has access.",
  "externalEntry.recognition.error.promptUnavailable": "The AI instruction is unavailable or set up in a way this processing cannot use.",
  "externalEntry.recognition.error.providerRejected": "The AI service rejected the request.",
  "externalEntry.recognition.error.providerQuota": "The AI service's request limit or balance is used up.",
  "externalEntry.recognition.error.providerUnavailable": "The AI service was unavailable, and the automatic retries did not help.",
  "externalEntry.recognition.error.retryLimitReached": "The automatic retries are used up.",
  "externalEntry.recognition.error.callOutcomeUnknown": "It is not known how the AI request ended: the AI may already have answered and been paid for, so the request is not repeated automatically. Retry sends it again.",
  "externalEntry.recognition.error.resultInvalid": "The AI returned an answer that could not be read.",
  "externalEntry.recognition.error.internalError": "An internal server error occurred.",
  "externalEntry.recognition.error.unknown": "The processing stopped with an error.",
  "externalEntry.recognition.proposalsTitle": "Proposed entries",
  "externalEntry.recognition.proposalsNone": "The AI found no episodes of care in these documents.",
  "externalEntry.recognition.proposalsPending": "Proposed entries appear here as the AI fills them.",
  "externalEntry.recognition.proposalsStopped": "The processing stopped before it proposed any entries.",
  "externalEntry.recognition.untitled": "Untitled",
  "externalEntry.recognition.pagesRef": "p. {pages}",
  "externalEntry.recognition.pagesRead": "Pages read in full: {read} of {total}",
  "externalEntry.recognition.warningsCount": "AI remarks: {count}",
  "externalEntry.recognition.status.pending": "Awaiting review",
  "externalEntry.recognition.status.accepted": "Saved",
  "externalEntry.recognition.status.dismissed": "Dismissed",
  "externalEntry.recognition.savedAs": "Saved as {code}",
  "externalEntry.recognition.open": "Open",
  "externalEntry.recognition.dismiss": "Dismiss",
  "externalEntry.recognition.openNamed": "Open the proposed entry {name}",
  "externalEntry.recognition.dismissNamed": "Dismiss the proposed entry {name}",
  "externalEntry.recognition.dismissFailed": "The entry could not be dismissed.",
  "externalEntry.recognition.decidedConflict": "This entry was already decided; its current state is shown.",
  "externalEntry.recognition.proposalTitle": "Proposed entry",
  "externalEntry.recognition.warningsTitle": "AI remarks",
  "externalEntry.recognition.pagesPartial": "Pages read in full: {read} of {total}. The episode was too long to read whole — check the entry against the documents.",
  "externalEntry.recognition.decidedAccepted": "Saved as {code}: {name}, {at}",
  "externalEntry.recognition.decidedDismissed": "Dismissed: {name}, {at}",
  "externalEntry.recognition.cancelledReadOnly": "The processing was cancelled, so this entry can no longer be saved or dismissed.",
  "externalEntry.recognition.unsavedIntent": "Proposed entry — changes not saved",
  "externalEntry.recognition.notFound": "Proposed entry not found",
  "externalEntry.recognition.notFoundDetail": "Its processing may have been deleted.",
  "externalEntry.recognition.loading": "Loading the proposed entry…",
  "externalEntry.recognition.stripTitle": "Unfinished processings",
  "externalEntry.recognition.fileMark": "In a processing",
  "externalEntry.recognition.awaiting": "Proposed entries awaiting review: {count}",
} as const
